import gsap from "gsap";
import DefaultBlock from "/scripts/default/block";

export default class PricingPlans extends DefaultBlock {
	init() {
		this.selectors = {
			block: gsap.utils.selector(this.block),
		};

		const DOM = {
			panelWrap: this.selectors.block(".pricing-plans__wrap")[0],
			panels: this.selectors.block(".pricing-plans__panel"),
			license: this.selectors.block(".license-checkbox")[0],
			devInputs: this.selectors.block("[data-counter-input]"),
			devMinus: this.selectors.block("[data-counter-subtract]"),
			devPlus: this.selectors.block("[data-counter-add]"),
			pricingModal: this.selectors.block(
				"#tooltip-pricing-business-calculator"
			)[0],
			circles: this.selectors.block(".pricing-plans__flair--circles"),
			circlesPaths: this.selectors.block(".pricing-hero-circle-path"),
			star: this.selectors.block(".pricing-plans__flair--star"),
			popularBadge: this.selectors.block("#popular-badge")[0],
			popularBadgeCopy: this.selectors.block("#popular-badge .heading-s"),
			licenseBadge: this.selectors.block("#license-badge")[0],
			licenseBadgeCopy: this.selectors.block("#license-badge .heading-s"),
			featuresToggleBtn: this.selectors.block(
				"#pricing-plans-footer button"
			)[0],
			featuresTable: this.selectors.block("#features-table")[0],
			featuresTableInner: this.selectors.block("#features-table-inner")[0],
			currencySelect: this.selectors.block("#currency-select")[0],
			currencyDisplays: this.selectors.block(".js-currency-conversion"),
			coreDisplay: this.selectors.block(".js-currency-conversion")[0],
			plusDisplay: this.selectors.block(".js-currency-conversion")[1],
			premiumDisplay: this.selectors.block(".js-currency-conversion")[2],
			businessDisplay: this.selectors.block(".js-currency-conversion")[3],
			modalDisplay: this.selectors.block(".modal-currency-conversion")[0],
			submitLinks: this.selectors.block("[data-submit]"),
			stateLinks: this.selectors.block("[data-state]"),
			downgradeButtons: this.selectors.block('[data-js="downgrade-button"]'),
			mainButtons: this.selectors.block('[data-js="main-button"]'),
			activeButtons: this.selectors.block('[data-js="active-button"]'),
			renewals: this.selectors.block(".pricing-plans__renewals"),
			renewalPrices: this.selectors.block(".js-renewal"),
			frequencyPlus: this.selectors.block(".js-frequency-plus")[0],
			frequencyPremium: this.selectors.block(".js-frequency-premium")[0],
			frequencyBusiness: this.selectors.block(".js-frequency-business")[0],
			frequencyModal: this.selectors.block(".js-frequency-modal")[0],
			descriptionPlus: this.selectors.block(".js-description-plus")[0],
			descriptionPremium: this.selectors.block(".js-description-premium")[0],
			descriptionBusiness: this.selectors.block(".js-description-business")[0],
			discountPremium: this.selectors.block(".pricing-discount-premium")[0],
			discountBusiness: this.selectors.block(".pricing-discount-business")[0],
			discountModal: this.selectors.block(".pricing-discount-modal")[0],
			businessLicense: this.selectors.block(".js-business-license")[0],
			businessModal: this.selectors.block(
				"#tooltip-pricing-business-calculator"
			)[0],
		};
		this.DOM = DOM;

		const state = {
			developerCount: parseInt(this.DOM.devInputs[0].value),
			needsLicense: false,
			permanentLicense: false,
			currency: "USD",
			currencySymbol: "$",
			tier: "Core",
		};
		this.state = state;

		this.licenseToggle = this.licenseToggle.bind(this);
		this.keyUpdate = this.keyUpdate.bind(this);
		this.minusDeveloper = this.minusDeveloper.bind(this);
		this.plusDeveloper = this.plusDeveloper.bind(this);
		this.setGradients = this.setGradients.bind(this);
		this.toggleFeaturesTable = this.toggleFeaturesTable.bind(this);

		// just get the session info once
		this.possibleTiers = {};
		this.memberState = {};
		this.activeTier = {};
		this.businessUpgradeInfo = {};
		this.convertedAmounts = {};

		// the amounts to display - defaults but populated by user login state
		this.baseAmounts = {
			core: 0,
			plus: 99,
			plusOriginal: 99,
			premium: 149,
			premiumOriginal: 149,
			business: 199,
			businessOriginal: 199,
			renewals: 0,
		};

		this.initialLoad = true;

		this.fetchInitialState();

		const mm = gsap.matchMedia();

		mm.add(
			{
				isMobile: "(max-width: 1399px)",
				isDesktop: "(min-width: 1400px)",
				reduceMotion: "(prefers-reduced-motion: reduce)",
			},
			(context) => {
				let { isMobile, reduceMotion } = context.conditions;

				if (reduceMotion || isMobile) {
					gsap.to(".pricing-plans__panel", {
						opacity: 1,
					});

					return;
				}

				gsap.set(".pricing-plans__panel", {
					opacity: 0,
					yPercent: 10,
					transformOrigin: "bottom center",
				});

				gsap.set(this.DOM.panels[0], { rotation: -20 });
				gsap.set(this.DOM.panels[1], { rotation: -5 });
				gsap.set(this.DOM.panels[2], { rotation: 5 });
				gsap.set(this.DOM.panels[3], { rotation: 20 });

				gsap.to([this.DOM.panels[1], this.DOM.panels[2]], {
					rotation: 0,
					opacity: 1,
					yPercent: 0,
					duration: 0.8,
					ease: "back.out",
				});

				gsap.to([this.DOM.panels[0], this.DOM.panels[3]], {
					rotation: 0,
					opacity: 1,
					yPercent: 0,
					duration: 0.8,
					ease: "back.out",
					delay: 0.05,
				});
			}
		);
	}

	initEvents() {
		const mm = gsap.matchMedia();

		this.DOM.license.addEventListener("click", this.licenseToggle.bind(this));

		this.DOM.currencySelect.addEventListener(
			"change",
			this.makeCurrencyConversionRequest.bind(this)
		);

		this.DOM.stateLinks.forEach((el) => {
			el.addEventListener("click", (event) => this.updateTier(event, el));
		});

		this.DOM.submitLinks.forEach((el) => {
			el.addEventListener("click", (event) => this.checkout(event, el));
		});

		this.DOM.devInputs.forEach((el) => {
			el.addEventListener("change", this.keyUpdate);
		});

		this.DOM.devMinus.forEach((el) => {
			el.addEventListener("click", this.minusDeveloper);
		});

		this.DOM.devPlus.forEach((el) => {
			el.addEventListener("click", () => this.plusDeveloper(el));
		});

		this.DOM.mainButtons.forEach((el) => {
			el.addEventListener("click", () => this.updateTier(el));
		});

		this.DOM.featuresToggleBtn.addEventListener(
			"click",
			this.toggleFeaturesTable
		);

		mm.add(
			["(prefers-reduced-motion: no-preference)", "(min-width: 1240px)"],
			() => {
				this.DOM.panelWrap.addEventListener("mouseleave", () =>
					this.setGradients(4)
				);

				this.DOM.panels.forEach((panel, index) => {
					panel.addEventListener("mouseenter", () => this.setGradients(index));
				});
			}
		);
	}

	fetchInitialState(e) {
		const url = "/community/index.php?app=gspages&module=ajax&controller=api";
		const data = {
			query:
				"{privjs{pricing{id,name,developers,permanent,price,renewals,originalPrice},me{token,packageId,package,developers,renewals,permanent}}}",
		};

		fetch(url, {
			method: "POST",
			body: JSON.stringify(data),
		})
			.then((response) => response.json())
			.then((json) => {
				this.possibleTiers = json.privjs.pricing;
				//this.possibleTiers = pricing;
				this.memberState = json.privjs.me;
				//this.memberState = me;

				// Function to find and return the active subscription tier
				const findActiveTier = (memberState, possibleTiers) => {
					for (let index = 0; index < possibleTiers.length; index++) {
						const tier = possibleTiers[index];

						// Compare the formatted fields with memberState
						if (
							parseInt(tier.id) === parseInt(memberState.packageId) &&
							tier.permanent === memberState.permanent &&
							parseInt(tier.developers) === parseInt(memberState.developers)
						) {
							return tier;
						}
					}
					return null;
				};

				// Find the active tier
				this.activeTier = findActiveTier(this.memberState, this.possibleTiers);

				// set the state object based on active tier
				const setState = (activeTier, possibleTiers) => {
					if (activeTier) {
						// Set the active subscription info
						this.state.developerCount = parseInt(activeTier.developers);
						this.state.permanentLicense = activeTier.permanent === "1" ? true : false;
						activeTier.active = true;
					}
				};
				// Set the active tier and update state based on it
				setState(this.activeTier);

				// set the active tier from Esther's ID's
				// core
				// plus = 3
				// premium = 4
				// business = 5
				// business PERM = 8
				if (this.activeTier && this.activeTier.id) {
					this.state.tier = this.activeTier.id;
				} else {
					this.state.tier = "Core"
				    return
				}

				// if plus, grab the premium upgrade price. Plus always displays the same
				if(this.state.tier == "3") {
					this.baseAmounts.premium = parseFloat(this.possibleTiers[1].price);
				    this.baseAmounts.premiumOriginal = parseFloat(this.possibleTiers[1].originalPrice);
				}

				// set the UI to show correct dev count and update the price accordingly
				this.updateDeveloperCount();
				this.setActiveUI()
				this.updateBusinessPrices();
				this.updateBadges();
			})
			.catch((error) => {
				console.error("An error occurred:", error);
			});
	}

	updateTier(event, el) {
		this.state.tier = el.dataset.state;
	}

	setActiveUI() {
		let tier = this.state.tier

		if (tier === "3") {
			this.DOM.plusDisplay.innerText = "Active";

			this.DOM.panels[0].classList.add("downgrade");
			this.DOM.panels[1].classList.add("active");

			this.DOM.mainButtons[2].querySelector(".button__label").innerText =
				"Upgrade today";
			// show Business upgrade
			this.DOM.mainButtons[3].querySelector(".button__label").innerText =
				"Upgrade today";
			this.DOM.descriptionPlus.innerText =
				"You're part of the Club! Thanks. You have access to some of the bonus plugins.";
		}

		if (tier === "4") {
			this.DOM.premiumDisplay.innerText = "Active";

			this.DOM.panels[0].classList.add("downgrade");
			this.DOM.panels[1].classList.add("downgrade");
			this.DOM.panels[2].classList.add("active");

			this.DOM.descriptionPremium.innerText =
				"You're part of the Club! Thanks. You have access to all of the bonus plugins.";
			this.DOM.descriptionPremium.classList.add("pricing-gradient-text");

			// show Business upgrade
			this.DOM.mainButtons[3].style.display = "block";
			this.DOM.mainButtons[3].querySelector(".button__label").innerText =
				"Upgrade today";
		}

		if (tier === "5" || tier === "8") {
			this.DOM.panels[0].classList.add("downgrade");
			this.DOM.panels[1].classList.add("downgrade");
			this.DOM.panels[2].classList.add("downgrade");

			let devs = parseInt(this.state.developerCount);

			this.DOM.devInputs.forEach(input => {
				input.min = devs
			})
	
			// for the tiers that don't allow upgrades, show prompt them to contact us
			if (this.state.developerCount === 20 || this.activeTier.permanent === "1") {
				this.setBusinessActive()

				this.DOM.panels[3].classList.add("highest");

				this.DOM.descriptionBusiness.innerText = `Need to make changes to your plan? Drop us an email and we'll get right back to you.`;

				this.DOM.devPlus.forEach((el) => {
					el.dataset.disabled = true;
				});

				this.state.highestTier = true;

				return;
			} else {
				// if the user isn't on one of the highest tiers - add a developer to the shown UI to prompt an upgrade
				devs++
			}

			this.DOM.devInputs.forEach((input) => {
				input.value = devs;
			});
			this.state.developerCount = devs;


			// show Business upgrade
			this.DOM.mainButtons[3].querySelector(".button__label").innerText = "Upgrade today";

			this.updateBadges("upgrade");
		}
	}

	setBusinessActive(){
		this.DOM.panels[3].classList.add("active")
		this.DOM.pricingModal.classList.add("active")
		this.DOM.businessDisplay.innerText = "Active";
		this.DOM.modalDisplay.innerText = "Active";
		this.updateBadges("active");
	}

	// this passes over the current state and moves the user to the IPS checkout
	checkout(e) {
		if (this.state.tier == "Core") {
			return;
		}

		e.preventDefault();

		let queryParams = {};

		// plus and premium just need the tier #
		if (this.state.tier === "3" || this.state.tier === "4") {
			queryParams.package = this.state.tier
		}

		// business needs perm state too
		if (this.state.tier === "5" || this.state.tier === "8") {
			queryParams.users = this.state.developerCount;

			if (this.state.permanentLicense) {
				queryParams.permanent = true;
			}
		}

		const baseUrl =
			"/community/index.php?app=gscheckout&module=products&controller=users&do=buy";

		const queryString = Object.entries(queryParams)
			.map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
			.join("&");

		const fullUrl = `${baseUrl}&${queryString}`;

		window.location.href = fullUrl
	}

	updateLicenseURL(e) {
		if (!this.state.permanentLicense && this.state.developerCount == 1) {
			this.DOM.businessLicense.setAttribute(
				"href",
				"/community/license?license=business"
			);
		} else {
			let perm = this.state.permanentLicense ? "permanent" : "annual";
			this.DOM.businessLicense.setAttribute(
				"href",
				`/community/license?license=business-${this.state.developerCount}-${perm}`
			);
		}
	}

	returnPrices(developerCount, permanent) {
		const matchingProduct = this.possibleTiers.find((product) => {
			return (
				product.id !== "3" &&
				product.id !== "4" &&
				parseInt(product.developers) === developerCount &&
				(permanent === true
					? product.permanent === "1"
					: product.permanent === "")
			);
		});

		if (matchingProduct) {
			return {
				price: parseFloat(matchingProduct.price),
				renewals: parseFloat(matchingProduct.renewals),
				originalPrice: parseFloat(matchingProduct.originalPrice),
				active: matchingProduct.active
			};
		} else {
			return null;
		}
	}

	makeCurrencyConversionRequest(e) {
		let currencyCode = this.DOM.currencySelect.value;
		this.state.currency = currencyCode;

		function fetchCurrencyRate(currency) {
			const apiKey = "8b5911a58923453f86137d84ff1a99db";

			const query = "USD" + "_" + currency;

			const url = `https://api.currconv.com/api/v7/convert?q=${query}&compact=ultra&apiKey=${apiKey}`;

			fetch(url)
				.then((response) => {
					if (!response.ok) {
						throw new Error(`HTTP error! Status: ${response.status}`);
					}
					return response.json();
				})
				.then((data) => {
					updatePrices(data, currencyCode);
				})
				.catch((error) => {
					console.log("Fetch error: ", error);
				});
		}

		fetchCurrencyRate(currencyCode);

		const updatePrices = (data, currencyCode) => {
			const currencyKey = `USD_${currencyCode}`;
			const conversionRate = data[currencyKey];

			const currencySymbols = {
				AUD: "$", // Australian Dollar
				CAD: "$", // Canadian Dollar
				CNY: "¥", // Chinese Yuan
				EUR: "€", // Euro
				GBP: "£", // British Pound Sterling
				INR: "₹", // Indian Rupee
				JPY: "¥", // Japanese Yen
				KRW: "₩", // South Korean Won
				MXN: "$", // Mexican Peso
				NZD: "$", // New Zealand Dollar
				RUB: "₽", // Russian Ruble
				SGD: "$", // Singapore Dollar
				TWD: "NT$", // Taiwan Dollar
				USD: "$", // United States Dollar
			};

			this.state.currencySymbol = currencySymbols[currencyCode];
			let baseAmounts = this.baseAmounts;

			for (const tier in baseAmounts) {
				if (baseAmounts.hasOwnProperty(tier)) {
					const baseAmount = baseAmounts[tier];
					
					const num = baseAmount * conversionRate
					const convertedAmount = parseFloat(num.toFixed(2))
					this.convertedAmounts[tier] = convertedAmount;
				}
			}

			this.displayPrices(this.convertedAmounts);
		};
	}

	displayOngoing(original, price, el) {
		let currency = this.state.currencySymbol;

		let o = original;
		let p = price;
		if (o > p) {
			let amount = o - p

			el.style.display = "block";
			el.innerText = `Discounted price from your existing membership. Then ${currency}${original} / year`;

			if(el === this.DOM.discountPremium) {
				this.DOM.frequencyPremium.innerText = "today";
			} else {
				this.DOM.frequencyModal.innerText = "today";
				this.DOM.frequencyBusiness.innerText = "today";
			}

			

			this.updateBadges("discount", amount.toFixed(2))
		}
	}

	displayPrices(prices) {
		let currency = this.state.currencySymbol;

		let longestString = `${currency}${prices.business}`;

		// if it's gonna wrap - bring the font down
		longestString.length > 6
			? this.DOM.panelWrap.classList.add("reduceSize")
			: this.DOM.panelWrap.classList.remove("reduceSize");

		// Always use the original price for plus and premium
		this.animateText(this.DOM.plusDisplay, currency, prices.plusOriginal);
		this.animateText(this.DOM.premiumDisplay, currency, prices.premium);
		this.animateText(this.DOM.businessDisplay, currency, prices.business);

		let busPrice = parseFloat(prices.business);
		let formattedBusinessPrice = Number.isInteger(busPrice) ? busPrice : busPrice.toFixed(2);
	    // immediately set the content in the modal on load
		if(this.initialLoad) {
			this.DOM.modalDisplay.innerText = `${currency}${formattedBusinessPrice}`
		} else {
			// animate the numbers to change
			// only animate the modal if it's open 
			if (this.DOM.businessModal.classList.contains("is-open")) {
				this.animateText(this.DOM.modalDisplay, currency, prices.business);
				this.DOM.businessDisplay.innerText = `${currency}${formattedBusinessPrice}`;
			} else {
				this.animateText(this.DOM.businessDisplay, currency, prices.business);
				this.DOM.modalDisplay.innerText = `${currency}${formattedBusinessPrice}`;
			}
		}
		this.initialLoad = false; // stops the text flash and count animation on the first load

		// bang in some renewal costs
		this.DOM.renewalPrices.forEach((el) => {
			el.innerText = `${currency}${prices.renewals}`;
		});

		// display a discount if applicable to premium
		this.displayOngoing(
			prices.premiumOriginal,
			prices.premium,
			this.DOM.discountPremium
		);

		if (!this.state.permanentLicense) {
			this.displayOngoing(
				prices.businessOriginal,
				prices.business,
				this.DOM.discountBusiness
			);

			this.displayOngoing(
				prices.businessOriginal,
				prices.business,
				this.DOM.discountModal
			);
		}
	}

	animateText(el, currency, price) {
		gsap.to(el, {
			innerText: price,
			onUpdate: currencyFormatter(el, currency), // handles rounding and making sure the currency character stays in place
			duration: 0.6,
		});

		function currencyFormatter(el, currency) {
			typeof el === "string" && (el = gsap.utils.toArray(el)[0]);

			return () => {
				let text = el.innerText;
				let num = parseFloat(text);
				let formattedNum = Number.isInteger(num) ? num : num.toFixed(2);
				el.innerText = currency + formattedNum;
			};
		}
	}

	updateBusinessPrices() {
		if(this.state.highestTier === true) return;
		
		let { price, renewals, originalPrice, active } = this.returnPrices(
			this.state.developerCount,
			this.state.permanentLicense
		);

		// save them to the base state for conversions
		this.baseAmounts.business = price.toFixed(2);

		this.baseAmounts.renewals = renewals;
		this.baseAmounts.businessOriginal = originalPrice;
		this.state.activeSelected = active;

		if(active) {
			this.setBusinessActive()
			return
		} else {
			this.DOM.panels[3].classList.remove("active")
			this.DOM.pricingModal.classList.remove("active")
		}

		// update the business license link
		this.updateLicenseURL();

		if (this.state.currency == "USD" ) {
			this.displayPrices(this.baseAmounts);
		} else {
			this.makeCurrencyConversionRequest();
		}
	}

	// Events
	minusDeveloper() {
		if (
			(this.state.tier === "5" || this.state.tier === "8") &&
			this.state.developerCount <= parseInt(this.memberState.developers)
		) {
			MicroModal.show("tooltip-pricing-downgrade");
			this.DOM.devMinus.forEach((el) => {
				el.dataset.disabled = true;
			});
			return;
		}

		if (this.state.developerCount <= parseInt(this.memberState.developers)) {
			MicroModal.show("tooltip-pricing-downgrade");
			this.DOM.devMinus.forEach((el) => {
				el.dataset.disabled = true;
			});
			return;
		}
		if (this.state.developerCount > 1) {
			this.state.developerCount--;
			this.updateUI();
		}
		if (this.state.developerCount < 20) {
			this.DOM.devPlus.forEach((el) => {
				el.dataset.disabled = false;
			});
		}
		if (this.state.developerCount === 1) {
			this.DOM.devMinus.forEach((el) => {
				el.dataset.disabled = true;
			});
		}
	}

	plusDeveloper(el) {
		if (this.state.developerCount >= 20 || el.dataset.disabled === "true") {
			MicroModal.show("tooltip-pricing-upgrade");
			return;
		}
		if (this.state.developerCount >= 19) {
			MicroModal.show("tooltip-pricing-upgrade");
			this.DOM.devPlus.forEach((el) => {
				el.dataset.disabled = true;
			});
		}
		if (this.state.developerCount > 0) {
			this.DOM.devMinus.forEach((el) => {
				el.dataset.disabled = false;
			});
		}

		this.state.developerCount++;
		this.updateUI();
	}

	keyUpdate(e) {
		const count = parseInt(e.target.value);

		if(count < +this.memberState.developers) {
			this.state.developerCount = +this.memberState.developers
			this.updateUI();
			MicroModal.show("tooltip-pricing-downgrade");
			return;
		}

		if(count > 20) {
			this.state.developerCount = 20
			this.updateUI();
			MicroModal.show("tooltip-pricing-upgrade");
			return;
		}

		this.state.developerCount = count > 0 ? count : 1;
		this.updateUI();
	}

	updateDeveloperCount() {
		this.DOM.devInputs.forEach((el) => {
			el.value = this.state.developerCount;
		});
	}

	licenseToggle(e) {
		e.preventDefault();
		this.state.needsLicense = !this.state.needsLicense;

		this.DOM.license.setAttribute("checked", this.state.needsLicense);
		this.DOM.license.checked = this.state.needsLicense;

		this.updateBadges();
	}

	updateBadges(message, discount) {
		const userNeedsBusiness =
			this.state.developerCount > 1 || this.state.needsLicense || this.state.tier === "5" || this.state.tier === "4" || this.state.highestTier;

		this.DOM.popularBadge.setAttribute("data-hidden", userNeedsBusiness);
		this.DOM.licenseBadge.setAttribute("data-hidden", !userNeedsBusiness);

		if (message == "upgrade") {
			this.DOM.licenseBadgeCopy[0].innerText = "Upgrade";
			this.DOM.licenseBadgeCopy[1].innerText = "Today!";
		} else if (message === "active") {
			this.DOM.licenseBadgeCopy[0].innerText = "Active";
			this.DOM.licenseBadgeCopy[1].innerText = "Level";
		} else if (message === "discount") {
			this.DOM.licenseBadgeCopy[0].innerText = `${this.state.currencySymbol}${discount}`;
			this.DOM.licenseBadgeCopy[1].innerText = "discount!";
			this.DOM.popularBadgeCopy[0].innerText = `${this.state.currencySymbol}${discount}`;
			this.DOM.popularBadgeCopy[1].innerText = "discount!";
		}
	}

	updateUI(){
		this.updateDeveloperCount();
		this.updateBusinessPrices();
		this.updateBadges();
	}

	setGradients(index) {
		const defaults = {
			duration: 1,
			ease: "power3.out",
		};

		gsap.to(this.DOM.circlesPaths, {
			opacity: 0,
			...defaults,
		});
		gsap.to(this.DOM.circlesPaths[index], {
			opacity: 1,
			...defaults,
		});
	}

	toggleFeaturesTable() {
		const activeClass = "is-active";

		if (this.DOM.featuresToggleBtn.classList.contains(activeClass)) {
			this.DOM.featuresToggleBtn.classList.remove(activeClass);

			gsap.to(this.DOM.featuresTable, {
				height: 0,
				ease: "power2.inOut",
				duration: 0.3,
				onComplete: () => gsap.set(this.DOM.featuresTable, { display: "none" }),
			});
		} else {
			this.DOM.featuresToggleBtn.classList.add(activeClass);

			gsap.set(this.DOM.featuresTable, { display: "block" });
			gsap.to(this.DOM.featuresTable, {
				height: this.DOM.featuresTableInner.scrollHeight,
				ease: "power2.inOut",
				duration: 0.3,
			});
		}
	}
}
